/**
 * roleHelpers.js — iter269 (frontend twin of services/role_helpers.py)
 *
 * Shared, case-insensitive, multi-role safe helpers for every place in
 * the UI that needs to answer "does this user have role X?". Do NOT
 * inline `user.role === 'qc'` anywhere; import from here so a new role
 * added to the system needs zero code changes on the client.
 */

/** Returns the lowercased set of roles attached to `user`.
 *
 * Accepts three shapes for future-proofing (iter279):
 *   1. `user.roles = ['cx', 'qc']`  — array of strings
 *   2. `user.role  = 'cx,qc'`       — comma-separated string
 *   3. `user.role  = 'cx'`          — plain string
 */
export function userRoleSet(user) {
  if (!user || typeof user !== 'object') return new Set();
  const push = (out, val) => {
    const s = String(val || '').trim().toLowerCase();
    if (s) out.add(s);
  };
  const out = new Set();
  const raw = user.roles;
  if (Array.isArray(raw) && raw.length) {
    raw.forEach(r => push(out, r));
    if (out.size) return out;
  }
  const scalar = user.role;
  if (typeof scalar === 'string' && scalar.trim()) {
    // Split on comma/semicolon/whitespace so `"cx, qc"` and `"cx qc"` both work.
    scalar.split(/[,;\s]+/).forEach(r => push(out, r));
  }
  return out;
}

/** True iff the (case-insensitive) `role` slug is one of the user's roles. */
export function userHasRole(user, role) {
  if (!role) return false;
  return userRoleSet(user).has(String(role).trim().toLowerCase());
}

/** True iff the user has at least one of the given roles. */
export function userHasAnyRole(user, roles) {
  if (!Array.isArray(roles) || roles.length === 0) return false;
  const own = userRoleSet(user);
  return roles.some(r => own.has(String(r || '').trim().toLowerCase()));
}
